/**
 * A made-up points table, kept in the URL as `pts` in the one-line form the
 * API reads: `race:25,18,15;qualifying:3,2,1;fastest_lap:1`. Tables saved under
 * a name live in this browser's local storage, which is only a shelf to pick
 * them from: the table being shown is always the one in the URL.
 */

import { shelf } from './shelf'

export const FIELDS = [
  { key: 'race', label: 'Race' },
  { key: 'sprint', label: 'Sprint' },
  { key: 'qualifying', label: 'Qualifying' },
  { key: 'practice', label: 'Free practice' },
  { key: 'fastest_lap', label: 'Fastest lap' },
  { key: 'final', label: 'Last race ×' },
] as const

export type Field = (typeof FIELDS)[number]['key']

/**
 * Each field as typed: points by position, comma-separated; one value for the
 * fastest lap, and one for what the season's last round is multiplied by.
 */
export type Scale = Record<Field, string>

export const EMPTY: Scale = {
  race: '',
  sprint: '',
  qualifying: '',
  practice: '',
  fastest_lap: '',
  final: '',
}

/** Every table a season has been scored on, each once, by the years it ran. */
export const HISTORY: [name: string, text: string][] = [
  ['1950–1959', 'race:8,6,4,3,2;fastest_lap:1'],
  ['1960', 'race:8,6,4,3,2,1'],
  ['1961–1990', 'race:9,6,4,3,2,1'],
  ['1991–2002', 'race:10,6,4,3,2,1'],
  ['2003–2009', 'race:10,8,6,5,4,3,2,1'],
  ['2010–2013, 2015–2018', 'race:25,18,15,12,10,8,6,4,2,1'],
  ['2014', 'race:25,18,15,12,10,8,6,4,2,1;final:2'],
  ['2019–2020', 'race:25,18,15,12,10,8,6,4,2,1;fastest_lap:1'],
  ['2021', 'race:25,18,15,12,10,8,6,4,2,1;sprint:3,2,1;fastest_lap:1'],
  ['2022–2024', 'race:25,18,15,12,10,8,6,4,2,1;sprint:8,7,6,5,4,3,2,1;fastest_lap:1'],
  ['2025–', 'race:25,18,15,12,10,8,6,4,2,1;sprint:8,7,6,5,4,3,2,1'],
]

const KEY = 'pts'

function values(text: string): string[] {
  return text
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
}

/** Never throws: an unknown key or a stray piece is dropped, as a damaged link should be. */
export function parseScale(text: string): Scale {
  const scale = { ...EMPTY }
  for (const piece of text.split(';')) {
    const [key, ...rest] = piece.split(':')
    const field = FIELDS.find((entry) => entry.key === key?.trim())
    if (field && rest.length) scale[field.key] = values(rest.join(':')).join(', ')
  }
  return scale
}

/** The one-line form, fields left blank omitted, so equal tables write equal links. */
export function serialiseScale(scale: Scale): string {
  return FIELDS.flatMap(({ key }) => {
    const list = values(scale[key])
    return list.length ? [`${key}:${list.join(',')}`] : []
  }).join(';')
}

/** What is wrong with a table, or null. The server checks again; this is for the form. */
export function problem(scale: Scale): string | null {
  let scores = false
  for (const { key, label } of FIELDS) {
    const numbers = values(scale[key]).map(Number)
    if (numbers.some((value) => !Number.isFinite(value) || value < 0))
      return `${label}: points must be numbers, zero or more.`
    if ((key === 'fastest_lap' || key === 'final') && numbers.length > 1)
      return `${label}: one value only.`
    if (numbers.length > 40) return `${label}: at most 40 positions score.`
    if (key !== 'final') scores ||= numbers.some((value) => value > 0)
  }
  return scores ? null : 'The table gives no points at all.'
}

/** A table in words: `Race 25, 18 · Fastest lap 1`. */
export function describe(scale: Scale): string {
  return FIELDS.filter(({ key }) => scale[key])
    .map(({ key, label }) => `${label} ${scale[key]}`)
    .join(' · ')
}

export function readScale(params: URLSearchParams): Scale {
  return parseScale(params.get(KEY) ?? '')
}

/** `params` with `scale` written over `pts`, everything else kept. */
export function writeScale(params: URLSearchParams, scale: Scale): URLSearchParams {
  const updated = new URLSearchParams(params)
  const text = serialiseScale(scale)
  if (text) updated.set(KEY, text)
  else updated.delete(KEY)
  return updated
}

/** `pts` alone, as a `?…` string a link can carry from page to page. */
export function scaleSearch(params: URLSearchParams): string {
  const text = params.get(KEY)
  return text ? `?${new URLSearchParams({ [KEY]: text }).toString()}` : ''
}

// --- the shelf of named tables ------------------------------------------------

const SHELF = shelf('f1cal:scales')

/** Every saved table, by name. Empty when storage is unavailable. */
export const listSaved = SHELF.list

/** Saves under `name`, replacing a table of the same name. False when storage refused. */
export function saveScale(name: string, scale: Scale): boolean {
  return SHELF.save(name, serialiseScale(scale))
}

export const removeSaved = SHELF.remove
